import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  Plus,
  CheckCircle2,
  Pill,
  AlertTriangle,
  Loader2,
} from 'lucide-react';
import { Card, TabBar, Badge, AIPanel } from '@/components';
import {
  usePatientSummary,
  useCareGaps,
  usePatientQuestionnaireResponses,
  useCarePlan,
} from '@/fhir/hooks';
import {
  mapPatient,
  mapConditions,
  mapMedications,
  mapAllergies,
  mapLabs,
  mapVitals,
  mapQuestionnaireResponses,
  mapCarePlanActivities,
} from '@/fhir/mappers';
import { FhirErrorState } from '@/fhir/errors';
import { useSessionStore } from '@/stores/sessionStore';
import { useUiModeFlags } from '@/auth/useUiMode';
import { useTranscriptStream } from '@/firebase/useTranscriptStream';

// Status badge colors
const getStatusColor = (status) => {
  const colors = {
    active: 'bg-blue-500/10 text-blue-300 border-blue-500/20',
    normal: 'bg-green-500/10 text-green-300 border-green-500/20',
    high: 'bg-red-500/10 text-red-300 border-red-500/20',
    elevated: 'bg-amber-500/10 text-amber-300 border-amber-500/20',
    overdue: 'bg-red-500/10 text-red-300 border-red-500/20',
    due: 'bg-amber-500/10 text-amber-300 border-amber-500/20',
    pending: 'bg-slate-700/50 text-slate-300 border-slate-600/50',
    'in-progress': 'bg-blue-500/10 text-blue-300 border-blue-500/20',
    recommended: 'bg-amber-500/10 text-amber-300 border-amber-500/20',
  };
  return colors[status] || 'bg-slate-700/50 text-slate-300 border-slate-600/50';
};

// Chart Tab — renders real FHIR data shapes from src/fhir/mappers.js.
// Each section degrades gracefully to an empty-state line.
const ChartTab = ({ problems, medications, allergies, labs, vitals }) => (
  <div className="space-y-4">
    {/* Problems */}
    <Section title="Active Problems" empty="No active problems on file.">
      {problems.map((problem) => (
        <div
          key={problem.id}
          className="flex items-center justify-between p-3 bg-slate-800/50 border border-slate-700 rounded-lg"
        >
          <div>
            <p className="text-sm font-medium text-white">{problem.name}</p>
            {problem.onsetDate && (
              <p className="text-xs text-slate-500">Since {problem.onsetDate}</p>
            )}
          </div>
          <Badge variant="secondary" size="sm">
            {problem.status === 'active' ? 'Active' : problem.status}
          </Badge>
        </div>
      ))}
    </Section>

    {/* Medications */}
    <Section title="Current Medications" empty="No active medications on file.">
      {medications.map((med) => (
        <div
          key={med.id}
          className="flex items-center justify-between p-3 bg-slate-800/50 border border-slate-700 rounded-lg"
        >
          <div>
            <p className="text-sm font-medium text-white">{med.name}</p>
            <p className="text-xs text-slate-500">
              {[med.dose, med.frequency, med.route].filter(Boolean).join(' • ')}
            </p>
          </div>
          <Pill className="w-4 h-4 text-blue-400" />
        </div>
      ))}
    </Section>

    {/* Allergies — hide the section entirely when none on file */}
    {allergies.length > 0 && (
      <Section title="Allergies">
        {allergies.map((allergy) => (
          <div
            key={allergy.id}
            className={`p-3 rounded-lg border flex items-start gap-3 ${getStatusColor(
              allergy.severity
            )}`}
          >
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium">{allergy.name}</p>
              <p className="text-xs opacity-80">{allergy.reaction}</p>
            </div>
          </div>
        ))}
      </Section>
    )}

    {/* Recent Labs */}
    <Section title="Recent Labs" empty="No recent labs on file.">
      {labs.map((lab) => (
        <div
          key={lab.id}
          className="flex items-center justify-between p-3 bg-slate-800/50 border border-slate-700 rounded-lg"
        >
          <div>
            <p className="text-sm font-medium text-white">{lab.test}</p>
            <p className="text-xs text-slate-500">{lab.date}</p>
          </div>
          <div className="text-right">
            <p className={`text-sm font-mono font-semibold ${
              lab.status === 'normal' ? 'text-green-400' : 'text-red-400'
            }`}>
              {lab.value} {lab.unit}
            </p>
          </div>
        </div>
      ))}
    </Section>

    {/* Vitals */}
    <Section title="Vitals" empty="No vitals recorded for this patient.">
      {vitals.map((vital) => (
        <div
          key={vital.id}
          className="flex items-center justify-between p-3 bg-slate-800/50 border border-slate-700 rounded-lg"
        >
          <div>
            <p className="text-sm font-medium text-white">{vital.name}</p>
            <p className="text-xs text-slate-500">{vital.timestamp}</p>
          </div>
          <p className={`text-sm font-mono font-semibold ${
            vital.status === 'normal' ? 'text-green-400' : 'text-amber-400'
          }`}>
            {vital.value} {vital.unit}
          </p>
        </div>
      ))}
    </Section>
  </div>
);

function Section({ title, children, empty }) {
  const list = React.Children.toArray(children);
  return (
    <div>
      <h4 className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-3">
        {title}
      </h4>
      {list.length > 0 ? (
        <div className="space-y-2">{list}</div>
      ) : (
        empty && (
          <p className="text-xs text-slate-500 italic">{empty}</p>
        )
      )}
    </div>
  );
}

// Care Gaps Tab — TV-P1-05: HEDIS MeasureReport gaps with heuristic fallback.
const CareGapsTab = ({ patientId }) => {
  const { data, isLoading, error, refetch } = useCareGaps(patientId);
  const gaps = data?.gaps || [];
  const source = data?.source;

  if (!patientId) {
    return <ChartEmpty message="No patient in context yet." />;
  }
  if (isLoading) {
    return <ChartLoading />;
  }
  if (error) {
    return <FhirErrorState error={error} onRetry={() => refetch()} />;
  }
  if (gaps.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4 text-center text-slate-500">
        <CheckCircle2 className="w-7 h-7 mb-3 text-green-400" />
        <p className="text-sm font-medium text-white mb-1">No open care gaps</p>
        <p className="text-xs text-slate-500">
          Either every recommended measure is satisfied, or this tenant
          hasn't produced a MeasureReport for this patient yet.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {source === 'heuristic' && (
        <p className="text-[11px] text-amber-400/80 italic">
          Showing condition-based recommendations — no HEDIS MeasureReports
          available for this patient yet.
        </p>
      )}
      {gaps.map((gap) => (
        <div
          key={gap.id}
          className={`p-4 rounded-lg border ${
            gap.status === 'overdue'
              ? 'bg-red-500/10 border-red-500/20'
              : gap.status === 'due'
                ? 'bg-amber-500/10 border-amber-500/20'
                : 'bg-slate-800/50 border-slate-700'
          }`}
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <p className="font-medium text-white mb-1">{gap.gap}</p>
              {gap.lastDate && (
                <p className="text-xs text-slate-400">Last assessed: {gap.lastDate}</p>
              )}
            </div>
            <Badge
              variant={
                gap.status === 'overdue' ? 'destructive' : gap.priority === 'high' ? 'warning' : 'secondary'
              }
              size="sm"
            >
              {gap.status}
            </Badge>
          </div>
        </div>
      ))}
    </div>
  );
};

// Assess Tab — TV-P1-09: lists every QuestionnaireResponse for the patient.
const AssessTab = ({ patientId, encounterId }) => {
  const navigate = useNavigate();
  const { data, isLoading, error, refetch } = usePatientQuestionnaireResponses(patientId);
  const assessments = React.useMemo(() => mapQuestionnaireResponses(data), [data]);

  if (!patientId) return <ChartEmpty message="No patient in context yet." />;
  if (isLoading) return <ChartLoading />;
  if (error) return <FhirErrorState error={error} onRetry={() => refetch()} />;

  return (
    <div className="space-y-3">
      {assessments.length === 0 ? (
        <p className="text-xs text-slate-500 italic">
          No assessments on file for this patient yet. Start one below to
          collect structured screening data.
        </p>
      ) : (
        assessments.map((a) => {
          const pct = a.totalItems > 0 ? Math.round((a.completedItems / a.totalItems) * 100) : 0;
          return (
            <div key={a.id} className="p-4 bg-slate-800/50 border border-slate-700 rounded-lg">
              <div className="flex items-start justify-between mb-2">
                <p className="font-medium text-white">{a.name}</p>
                <Badge
                  variant={a.status === 'in-progress' ? 'default' : a.status === 'completed' ? 'success' : 'secondary'}
                  size="sm"
                >
                  {a.statusLabel}
                </Badge>
              </div>
              {a.totalItems > 0 && (
                <div className="w-full bg-slate-700 rounded-full h-2 mb-2">
                  <div
                    className="bg-blue-500 h-2 rounded-full"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              )}
              <p className="text-xs text-slate-500">
                {a.createdAt}
                {a.totalItems > 0 && ` • ${a.completedItems} / ${a.totalItems} items`}
              </p>
            </div>
          );
        })
      )}

      {/* Was a dead button (no onClick) — e2e smoke gap #14. The SDC
          assessment engine lives at /assessment/:encounterId (step 4
          in the top nav); navigate there like the nav step does. */}
      <button
        onClick={() => encounterId && navigate(`/assessment/${encounterId}`)}
        disabled={!encounterId}
        className="w-full p-3 border-2 border-dashed border-slate-600 hover:border-blue-500 rounded-lg text-slate-400 hover:text-blue-400 transition-colors flex items-center justify-center gap-2 mt-4 disabled:opacity-40"
      >
        <Plus className="w-4 h-4" />
        <span className="text-sm font-medium">Start Assessment</span>
      </button>
    </div>
  );
};

// Plan Tab — TV-P1-09: reads the active CarePlan's activity[] for the
// current encounter (falls back to patient-level active CarePlan).
const PlanTab = ({ patientId, encounterId }) => {
  const navigate = useNavigate();
  const { data, isLoading, error, refetch } = useCarePlan(encounterId, patientId);
  // useCarePlan returns a search Bundle (_count: 1) — pull the first
  // resource so the mapper sees the CarePlan, not the Bundle.
  const carePlan = data?.entry?.[0]?.resource || null;
  const activities = React.useMemo(() => mapCarePlanActivities(carePlan), [carePlan]);

  if (!patientId) return <ChartEmpty message="No patient in context yet." />;
  if (isLoading) return <ChartLoading />;
  if (error) return <FhirErrorState error={error} onRetry={() => refetch()} />;

  return (
    <div className="space-y-3">
      {!carePlan ? (
        <p className="text-xs text-slate-500 italic">
          No active care plan for this patient yet. Add an order below to
          start one.
        </p>
      ) : (
        <>
          {carePlan.title && (
            <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-1">
              {carePlan.title}
            </p>
          )}
          {activities.length === 0 ? (
            <p className="text-xs text-slate-500 italic">
              Care plan has no activities recorded.
            </p>
          ) : (
            activities.map((item) => (
              <div key={item.id} className="p-3 bg-slate-800/50 border border-slate-700 rounded-lg flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-200">{item.text}</p>
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    {item.kind}
                    {item.status && item.status !== 'unknown' ? ` • ${item.status}` : ''}
                  </p>
                </div>
              </div>
            ))
          )}
        </>
      )}

      {/* Was a dead button (no onClick) — e2e smoke gap #14. Orders
          live in the care-plan builder at /careplan/:encounterId
          (step 5 in the top nav). */}
      <button
        onClick={() => encounterId && navigate(`/careplan/${encounterId}`)}
        disabled={!encounterId}
        className="w-full p-3 border-2 border-dashed border-slate-600 hover:border-blue-500 rounded-lg text-slate-400 hover:text-blue-400 transition-colors flex items-center justify-center gap-2 mt-4 disabled:opacity-40"
      >
        <Plus className="w-4 h-4" />
        <span className="text-sm font-medium">Add Order</span>
      </button>
    </div>
  );
};

// AI Notes Tab — TV-P7-07
//
// Compact live-transcript preview inside the visit sidebar. Shows the
// last N final chunks from the same useTranscriptStream subscription
// that powers /ai-docs (TV-P7-06). Generated SOAP lives in
// PostVisitSummary (TV-P7-09); this tab is for during-visit context.
const AINotesTab = ({ encounterId }) => {
  const { chunks, isStreaming, error } = useTranscriptStream(encounterId);
  // Finals only — interims are too noisy at this size. Trim to last 12
  // so the sidebar stays scannable; full transcript is one tab over.
  const finals = chunks.filter((c) => c.isFinal).slice(-12);

  return (
    <AIPanel
      title="Live Transcript"
      subtitle={isStreaming ? 'Listening…' : 'Idle — STT not active'}
    >
      {error && (
        <div className="text-xs text-red-400 p-2 bg-red-500/10 rounded mb-3">
          Failed to load transcript: {error.message}
        </div>
      )}
      {!error && finals.length === 0 && (
        <div className="text-xs text-slate-500 italic py-6 text-center">
          {isStreaming
            ? 'Listening — first words will appear here.'
            : 'Transcript starts when the call audio reaches the STT bridge.'}
        </div>
      )}
      <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
        {finals.map((chunk) => {
          const isProvider = (chunk.participantIdentity || '').startsWith('Practitioner/');
          return (
            <div key={chunk.id} className="text-xs">
              <div className="flex items-center gap-1.5 mb-0.5">
                <span className={`font-semibold ${isProvider ? 'text-blue-400' : 'text-green-400'}`}>
                  {isProvider ? 'Provider' : 'Patient'}
                </span>
                {chunk.languageCode && chunk.languageCode !== 'en-IN' && (
                  <span className="text-[10px] uppercase text-slate-500">
                    {chunk.languageCode}
                  </span>
                )}
              </div>
              <p className="text-slate-300 leading-snug">{chunk.text}</p>
            </div>
          );
        })}
      </div>
      {finals.length === 12 && (
        <p className="text-[10px] text-slate-500 mt-2 italic">
          Showing last 12 — full transcript on the AI Notes tab.
        </p>
      )}
    </AIPanel>
  );
};

// Chat Tab
const ChatTab = () => (
  <div className="flex flex-col h-full">
    <div className="flex-1 space-y-2 mb-4">
      <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-700">
        <p className="text-sm text-slate-300">Chat history will appear here during the visit</p>
      </div>
    </div>
    <div className="flex gap-2">
      <input
        type="text"
        placeholder="Send message..."
        className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
      />
      <button className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-2 transition-colors">
        Send
      </button>
    </div>
  </div>
);

export default function PatientPanel({ patientId: patientIdProp }) {
  const [activeTab, setActiveTab] = useState('chart');
  // TV-LM-3 — read the UI mode. In lite, the panel collapses to a
  // single identity strip (name, age, MRN, chief complaint) with no
  // tabs. The full Phase 1-5 surface stays the default.
  const { isLite } = useUiModeFlags();

  // Source of truth for the patient context: the prop if a parent
  // already routed an Encounter, otherwise the JWT-derived session
  // patient set by AuthProvider on launch.
  const sessionPatientId = useSessionStore(
    (s) => s.currentPatient || s.smartContext?.patientId || null,
  );
  const sessionEncounterId = useSessionStore((s) => s.currentEncounter);
  const patientId = patientIdProp || sessionPatientId;
  const encounterId = sessionEncounterId || null;

  const { data, isLoading, error, refetch } = usePatientSummary(patientId);

  // Memoize the mappers so we don't reshape on every render.
  const mapped = React.useMemo(() => {
    if (!data) {
      return {
        patient: null,
        problems: [],
        medications: [],
        allergies: [],
        labs: [],
        vitals: [],
      };
    }
    return {
      patient: mapPatient(data.patient),
      problems: mapConditions(data.conditions),
      medications: mapMedications(data.medications),
      allergies: mapAllergies(data.allergies),
      labs: mapLabs(data.labs),
      vitals: mapVitals(data.vitals),
    };
  }, [data]);

  const tabs = [
    { id: 'chart', label: 'Chart' },
    { id: 'gaps', label: 'Care Gaps' },
    { id: 'assess', label: 'Assess' },
    { id: 'plan', label: 'Plan' },
    { id: 'notes', label: 'AI Notes' },
    { id: 'chat', label: 'Chat' },
  ];

  // TV-LM-3 — Lite mode: collapsed identity strip only.
  // Renders the header but skips the TabBar + tab content. The
  // provider still sees who the patient is + a loading state while
  // the chart fetches, but doesn't get the Phase 1-5 sidebar
  // taking up half the screen.
  if (isLite) {
    return (
      <Card className="bg-slate-900 border-slate-800 flex flex-col overflow-hidden">
        {mapped.patient ? (
          <div className="px-4 py-3 flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-white">{mapped.patient.name}</p>
              <p className="text-xs text-slate-500">
                MRN {mapped.patient.mrn}
                {mapped.patient.age != null ? ` • Age ${mapped.patient.age}` : ''}
              </p>
            </div>
          </div>
        ) : isLoading ? (
          <div className="px-4 py-3">
            <Loader2 className="w-4 h-4 animate-spin text-slate-500" />
          </div>
        ) : (
          <div className="px-4 py-3">
            <p className="text-xs text-slate-500">No patient in context.</p>
          </div>
        )}
      </Card>
    );
  }

  return (
    <Card className="bg-slate-900 border-slate-800 flex flex-col h-full overflow-hidden">
      {/* Patient header — only render once the FHIR Patient resource lands */}
      {mapped.patient && (
        <div className="px-4 py-3 border-b border-slate-800 flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-white">{mapped.patient.name}</p>
            <p className="text-xs text-slate-500">
              MRN {mapped.patient.mrn}
              {mapped.patient.age != null ? ` • Age ${mapped.patient.age}` : ''}
            </p>
          </div>
          {mapped.patient.lastUpdated && (
            <p className="text-[10px] text-slate-500">
              Updated {mapped.patient.lastUpdated}
            </p>
          )}
        </div>
      )}

      <TabBar
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        className="border-b border-slate-800"
      />

      <div className="flex-1 overflow-y-auto p-4">
        {activeTab === 'chart' && (
          <>
            {!patientId && <ChartEmpty message="No patient in context yet." />}
            {patientId && isLoading && <ChartLoading />}
            {patientId && error && <FhirErrorState error={error} onRetry={() => refetch()} />}
            {patientId && !isLoading && !error && (
              <ChartTab
                problems={mapped.problems}
                medications={mapped.medications}
                allergies={mapped.allergies}
                labs={mapped.labs}
                vitals={mapped.vitals}
              />
            )}
          </>
        )}
        {activeTab === 'gaps' && <CareGapsTab patientId={patientId} />}
        {activeTab === 'assess' && <AssessTab patientId={patientId} encounterId={encounterId} />}
        {activeTab === 'plan' && <PlanTab patientId={patientId} encounterId={encounterId} />}
        {activeTab === 'notes' && <AINotesTab encounterId={encounterId} />}
        {activeTab === 'chat' && <ChatTab />}
      </div>
    </Card>
  );
}

// ── Chart tab states ───────────────────────────────────────────────────────

function ChartLoading() {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-slate-500">
      <Loader2 className="w-6 h-6 animate-spin mb-3" />
      <p className="text-xs">Loading patient chart…</p>
    </div>
  );
}

function ChartEmpty({ message }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-slate-500">
      <AlertCircle className="w-6 h-6 mb-3" />
      <p className="text-xs">{message}</p>
    </div>
  );
}

